// appLock.js — device-local app lock (Face ID / Touch ID with 4 digit PIN
// fallback). The lock is a property of THIS DEVICE, not the account: nothing
// here ever touches Supabase, localStorage, or any plain-text store. The PIN
// is stored as a salted PBKDF2-SHA256 hash in the iOS Keychain via
// @capgo/capacitor-native-biometric's credential store (one plugin covers both
// biometric verification and Keychain access).
//
// Grace period: a timestamp comparison, never a running timer. On resign
// active the wall-clock time is noted (in memory only — if iOS kills the app,
// the next launch is a cold launch and cold launches always lock); on resume
// the elapsed time decides. GRACE_MS backgrounding or more relocks, less does
// not.
//
// Per-user: each account's record is keyed by its Supabase user id, so a PIN
// SURVIVES sign-out (the same user reuses it next time with no setup) and a
// different user on the same phone gets their own. Account deletion removes only
// that user's record. Nothing about the PIN ever reaches the server.
//
// Failed attempts: persisted in the same Keychain record, so force-quitting
// the app mid-lockout does not reset the counter. At MAX_ATTEMPTS — and on an
// explicit "Forgot PIN" — the lock screen clears THIS user's record ('forgot_pin')
// and signs them out, so the recovery path is account credentials plus a fresh
// PIN. A normal sign-out keeps the PIN. No data is wiped.

import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { reportError } from '../sentry';
import { SANCTIONED_CLEAR_REASONS, readLockRecord, writeLockRecord, clearLockRecord } from './appLockStore';

// The per-user record keying (`to.scorify.applock.<uid>`), the legacy-record
// migration, and the sanctioned-clear rules live in appLockStore.js (pure, unit-
// tested). This file binds them to the native Keychain backend, PBKDF2 hashing,
// biometrics, and Sentry reporting. The upshot: a PIN is keyed by Supabase user id,
// so it SURVIVES sign-out (same user reuses it), a different user gets their own, and
// deletion removes only that user's record.
const PBKDF2_ITERATIONS = 150000;

export const MAX_ATTEMPTS = 5;
export const GRACE_MS = 30 * 1000;
// 4 digits paired with the 5-attempt lockout: the keyspace matters less than
// the attempt ceiling, and 4 is the platform-native muscle memory.
export const PIN_LENGTH = 4;
// Records written before 2026-09-21 were 6 digit PINs and carry no pinLength
// field; verification must keep honoring them.
const LEGACY_PIN_LENGTH = 6;

// The digit count a stored record's PIN actually has (legacy records lack the
// field). UI reads this so a legacy 6 digit PIN gets 6 dots and a 6 digit
// submit trigger, while new setups use PIN_LENGTH.
export const recordPinLength = (state) => state?.pinLength || LEGACY_PIN_LENGTH;

// ── Keychain backend (test seam) ──────────────────────────────────────────────
// All credential I/O routes through this one object. Production is the native
// Keychain via NativeBiometric; tests inject an in-memory store so the per-user
// record logic (persistence across sign-out, per-uid isolation, migration,
// sanctioned clears) is exercised against the REAL functions. Never set at runtime.
const nativeBackend = {
  get: (server) => NativeBiometric.getCredentials({ server }),
  set: (server, username, password) => NativeBiometric.setCredentials({ server, username, password }),
  del: (server) => NativeBiometric.deleteCredentials({ server }),
};
let _kc = nativeBackend;
let _forceCapable = false;
export function __setLockTestBackend(backend) { _kc = backend || nativeBackend; _forceCapable = !!backend; }

export const isLockCapable = () => Capacitor.isNativePlatform() || _forceCapable;

// ── Anti-bleed design ────────────────────────────────────────────────────────
// The iOS Keychain SURVIVES app deletion, so a reinstall could let a new account
// inherit the previous install's PIN. TWO guards now prevent that:
//  1. PER-USER KEYING (primary): each account's record lives at its own key,
//     `to.scorify.applock.<uid>`, so a different account simply has no record to
//     read — there is no shared blob to bleed. (This is also what lets a PIN
//     survive sign-out: the record is keyed by uid, not by session.)
//  2. ACCOUNT OWNERSHIP (defense-in-depth): every record is also stamped with the
//     Supabase user id that wrote it; resolveLockStateForUser refuses a record
//     whose owner is a different account.
//
// A previous version kept a "fresh install" marker in localStorage and deleted the
// Keychain record whenever that marker was missing. That was a data-loss bug
// (device-confirmed, TestFlight 1.1 (6), 2026-09-22): localStorage is strictly LESS
// durable than the Keychain it guarded — WKWebView can drop it without a reinstall —
// so a dropped marker looked like a fresh install and silently wiped a valid PIN.
// The marker is gone. A PIN is now ONLY cleared by: account deletion, the user
// turning it off, the Forgot-PIN / 5-attempt reset, or a genuine cross-account
// mismatch. A normal sign-out NEVER clears it.

// ── Keychain record ─────────────────────────────────────────────────────────
// One JSON blob: { v, enabled, ownerId, salt, hash, iterations, attempts,
// prompted }. `prompted` records that this device has seen the one-time setup
// offer, so declining it does not re-ask on every launch.

// Read THIS user's lock record (per-user key + legacy migration live in the store).
// Called with no userId only before auth (boot cover), where it reads the legacy key.
export async function readLockState(userId) {
  if (!isLockCapable()) return null;
  return readLockRecord(_kc, userId);
}

async function writeLockState(userId, state) {
  await writeLockRecord(_kc, userId, state);
}

// Clear THIS user's record. `reason` MUST be one of SANCTIONED_CLEAR_REASONS (a
// normal sign-out is NOT one — the PIN persists): an unsanctioned or missing reason
// is treated as a bug, REFUSED (fails safe: the lock is kept), and reported to
// Sentry, so an unexplained wipe can never silently delete the PIN.
export async function clearLockState(reason, userId) {
  if (!SANCTIONED_CLEAR_REASONS.has(reason)) {
    try {
      reportError(new Error(`appLock: clearLockState refused — unsanctioned reason "${reason ?? 'none'}"`),
        { op: 'appLock.clearLockState.unsanctioned', reason: String(reason ?? 'none') });
    } catch {}
    return; // refuse — do not wipe the PIN on an unexplained clear
  }
  if (!isLockCapable()) return;
  await clearLockRecord(_kc, reason, userId);
}

// ── PIN hashing ─────────────────────────────────────────────────────────────

const toHex = (buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');

async function derivePinHash(pin, saltHex, iterations) {
  const enc = new TextEncoder();
  const salt = new Uint8Array(saltHex.match(/.{2}/g).map((h) => parseInt(h, 16)));
  const key = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    key,
    256
  );
  return toHex(bits);
}

export const isValidPin = (pin, length = PIN_LENGTH) => typeof pin === 'string' && new RegExp(`^\\d{${length}}$`).test(pin);

// ── Setup / verify / disable ────────────────────────────────────────────────

// ownerId (the Supabase user id) binds the record to the account that wrote
// it. Every writer stamps it; resolveLockStateForUser refuses records whose
// owner is not the signed-in user. Belt to the install marker's suspenders:
// even if a record outlives its account by a path nobody predicted, it can
// never operate for a different account.
export async function setupPin(pin, ownerId) {
  if (!isLockCapable() || !isValidPin(pin) || !ownerId) return false;
  try {
    const saltBytes = new Uint8Array(16);
    crypto.getRandomValues(saltBytes);
    const salt = toHex(saltBytes);
    const hash = await derivePinHash(pin, salt, PBKDF2_ITERATIONS);
    await writeLockState(ownerId, { enabled: true, ownerId, salt, hash, iterations: PBKDF2_ITERATIONS, pinLength: PIN_LENGTH, attempts: 0, prompted: true });
    return true;
  } catch {
    return false;
  }
}

// Lock off, but remember the device was offered it. Used by "Skip for now"
// and the Profile toggle.
export async function disableLock(ownerId) {
  if (!isLockCapable() || !ownerId) return;
  try { await writeLockState(ownerId, { enabled: false, ownerId, prompted: true }); } catch {}
}

export async function markPrompted(ownerId) {
  if (!isLockCapable() || !ownerId) return;
  try {
    const state = await readLockState(ownerId);
    if (state?.enabled) return; // never downgrade an enabled lock
    await writeLockState(ownerId, { ...(state || {}), enabled: false, ownerId, prompted: true });
  } catch {}
}

// The read every signed-in surface should use: fresh-install check (inside
// readLockState), then ownership. A record owned by a different account, or
// by no account at all (legacy, pre-ownerId), is cleared and reported as no
// lock — the caller then treats the device as never set up. onCleared is an
// optional hook for the TEMP [LOCKSEQ] beat logger.
export async function resolveLockStateForUser(userId, onCleared) {
  // readLockState(userId) reads (and migrates) THIS user's own per-user record, so a
  // record keyed under a different uid is structurally invisible here — the per-user
  // key is the primary anti-bleed guard now. ownerId is kept as defense-in-depth.
  const state = await readLockState(userId);
  if (!state) return null;
  if (!userId) return state;
  if (!state.ownerId) {
    try { await writeLockState(userId, { ...state, ownerId: userId }); } catch {}
    try { onCleared?.('adopted_unowned'); } catch {}
    return { ...state, ownerId: userId };
  }
  if (state.ownerId !== userId) {
    await clearLockState('reinstall_or_foreign', userId);
    try { onCleared?.('owner_mismatch'); } catch {}
    return null;
  }
  return state;
}

// Returns { ok, attemptsLeft, lockedOut }. attempts persists across force
// quits because it lives in the Keychain record itself.
export async function verifyPin(pin, userId) {
  const state = await readLockState(userId);
  if (!state?.enabled || !state.salt || !state.hash) return { ok: true, attemptsLeft: MAX_ATTEMPTS, lockedOut: false };
  let ok = false;
  try {
    const hash = await derivePinHash(String(pin), state.salt, state.iterations || PBKDF2_ITERATIONS);
    ok = hash === state.hash;
  } catch {
    ok = false;
  }
  if (ok) {
    if (state.attempts) { try { await writeLockState(userId, { ...state, attempts: 0 }); } catch {} }
    return { ok: true, attemptsLeft: MAX_ATTEMPTS, lockedOut: false };
  }
  const attempts = (state.attempts || 0) + 1;
  try { await writeLockState(userId, { ...state, attempts }); } catch {}
  const left = Math.max(0, MAX_ATTEMPTS - attempts);
  return { ok: false, attemptsLeft: left, lockedOut: left <= 0 };
}

// ── Biometrics ──────────────────────────────────────────────────────────────

// { available, label } — label is display copy ("Face ID" / "Touch ID").
export async function biometricInfo() {
  if (!isLockCapable()) return { available: false, label: null };
  try {
    const result = await NativeBiometric.isAvailable();
    if (!result?.isAvailable) return { available: false, label: null };
    // biometryType: 1 = Touch ID, 2 = Face ID (plugin BiometryType enum)
    const label = result.biometryType === 1 ? 'Touch ID' : 'Face ID';
    return { available: true, label };
  } catch {
    return { available: false, label: null };
  }
}

export async function verifyBiometric(label) {
  try {
    await NativeBiometric.verifyIdentity({
      reason: 'Unlock Scorify',
      title: 'Unlock Scorify',
      subtitle: label ? `Use ${label} to continue` : undefined,
    });
    return true;
  } catch {
    return false; // cancelled or failed — the PIN pad is right there
  }
}

// ── Grace period ────────────────────────────────────────────────────────────
// In-memory on purpose: surviving suspension is exactly what a timestamp
// comparison gives us, and NOT surviving process death is correct (a cold
// launch must always lock).
//
// TWO signals, because the resign timestamp alone is not trustworthy.
// Capacitor's appStateChange rides willResignActive, and on SCREEN AUTO-LOCK
// the webview can be suspended before JS processes that bridge message; the
// event is queued and delivered on wake, right before didBecomeActive — so
// noteBackgrounded() stamps wake time, elapsed reads ~0, and the relock never
// fired (device regression, 2026-09-21: swipe-home relocked, screen auto-lock
// did not). The heartbeat closes that hole: a 5s ticker whose missed ticks
// measure how long JS was actually frozen. A late-delivered event cannot
// forge that gap, and wake-order races are covered from both sides — if the
// resumed tick runs first it records the suspension; if the app-state event
// runs first the raw tick gap is still stale and large.

let backgroundedAt = null;
const TICK_MS = 5000;
let lastTickAt = Date.now();
let lastSuspensionGap = 0;
let lastSuspensionAt = 0;
let ticker = null;

// Started by LockGate once per session (native + signed in). Runs for the
// app's whole foreground life; while the Face ID prompt is up the app is
// inactive but NOT suspended, so ticks continue and no suspension is
// recorded — our own prompt can never look like time away.
export const startGraceTicker = () => {
  lastTickAt = Date.now();
  if (ticker) return;
  ticker = setInterval(() => {
    const now = Date.now();
    const gap = now - lastTickAt;
    if (gap >= TICK_MS * 3) { lastSuspensionGap = gap; lastSuspensionAt = now; }
    lastTickAt = now;
  }, TICK_MS);
};

export const noteBackgrounded = () => { backgroundedAt = Date.now(); };

export const graceInfo = () => {
  const now = Date.now();
  const sinceBg = backgroundedAt == null ? null : now - backgroundedAt;
  const rawTickGap = now - lastTickAt;
  const recentSuspension = now - lastSuspensionAt < 10000 ? lastSuspensionGap : 0;
  const expired =
    (sinceBg != null && sinceBg >= GRACE_MS) ||
    rawTickGap >= GRACE_MS ||
    recentSuspension >= GRACE_MS;
  return { sinceBg, rawTickGap, recentSuspension, expired };
};

export const graceExpired = () => graceInfo().expired;

export const clearGrace = () => {
  backgroundedAt = null;
  lastTickAt = Date.now();
  lastSuspensionGap = 0;
  lastSuspensionAt = 0;
};

// ── LOCK PASSED SIGNAL (2026-10-28) ──────────────────────────────────────────
// LockGate (src/components/AppLock.jsx) publishes whether the app is past the
// lock, so work that must wait for it (push action buttons, pushActions.js)
// can. `window.__scorifyUnlocked` is the current answer (LockGate mounts
// before Scorify's effects run, so the event alone could be missed); the
// event announces changes. Web and demo never mount LockGate: the consumer
// treats a non-native platform as passed.
export const LOCK_PASSED_EVENT = 'scorify-lock-passed';
export function announceLockPassed(passed) {
  try { window.__scorifyUnlocked = passed === true; } catch {}
  try { window.dispatchEvent(new Event(LOCK_PASSED_EVENT)); } catch {}
}
